/** ----------------------------------------------  Color Types ---------------------------------------------- */
/** Base Colors */
export const BaseColorType = {
	Primary: '--dc-primary',
	Secondary: '--dc-secondary',
	Accent: '--dc-accent',
	Error: '--dc-error',
	Info: '--dc-info',
	Success: '--dc-success',
	Warn: '--dc-warn',
} as const;

export type BaseColorValue = (typeof BaseColorType)[keyof typeof BaseColorType];
export type BaseColorKeyType = keyof typeof BaseColorType;

/** Color Levels */
export const DcColorLevels = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;
export type DcColorLevel = (typeof DcColorLevels)[number];
export type DcColorLevelValue = `${BaseColorValue}-${DcColorLevel}`;
export type DcColorLevelKeyType = `${BaseColorKeyType}${DcColorLevel}`;

/** Color Level Type */
export const DcColorLevelType = DcColorLevels.reduce(
	(acc, level) => {
		Object.entries(BaseColorType).forEach(([key, value]) => {
			acc[`${key}${level}`] = `${value}-${level}`;
		});
		return acc;
	},
	{} as Record<DcColorLevelKeyType, DcColorLevelValue>,
);

/** Lighten Type */
export type DcColorLightenKeyType = `${BaseColorKeyType}Lighten`;
export type DcColorLightenValue = `${BaseColorValue}-lighten`;

export const DcColorLightenType = {
	...Object.fromEntries(
		Object.entries(BaseColorType).map(([key, value]) => {
			return [`${key}Lighten`, `${value}-lighten`];
		}),
	),
} as const;

export type DcColorLightenType = (typeof DcColorLightenType)[keyof typeof DcColorLightenType];

/** Combined Color Types */
export const DcColorType = {
	...BaseColorType,
	...DcColorLevelType,
	...DcColorLightenType,
} as const;
export type DcColorType = Lowercase<keyof typeof DcColorType> | (typeof DcColorType)[keyof typeof DcColorType];

/** Complete Color Key Types */
export type DcColorKeyType = Lowercase<keyof typeof DcColorType> | DcColorType;

/** ----------------------------------------------  Color Helpers ---------------------------------------------- */

export function getColorClass(color: DcColorType, element: 'bg' | 'border' | string): string {
	const key = getColorType(color)?.toLowerCase();
	return key ? `dc-${element}-${key}` : '';
}

export function getColor(color: DcColorType): string {
	const key = Object.keys(DcColorType).find(key => key.toLowerCase() === color.toLowerCase());
	if (!key) {
		return `var(${color})`;
	}
	return `var(${DcColorType[key as keyof typeof DcColorType]})`;
}

export function getColorType(color: DcColorType): DcColorKeyType | undefined {
	const key = Object.entries(DcColorType).find(([key, value]) => key.toLowerCase() === color.toLowerCase() || value === color);
	if (key) {
		return key[0].toLowerCase() as DcColorKeyType;
	}
	return undefined;
}
